import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { Breadcrumb } from './breadcrumb';

import type { SxProps, Theme } from '@mui/material/styles';

interface BreadcrumbItem {
  href?: string;
  label: string;
}

interface PageHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  leadingAction?: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
}

export function PageHeader({
  title,
  description,
  actions,
  leadingAction,
  breadcrumbs,
  className,
  sx,
  ...props
}: PageHeaderProps & { sx?: SxProps<Theme> }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: 3,
        mb: { xs: 2, md: 3 },
        ...sx
      }}
      className={className}
      {...props}
    >
      {leadingAction && <Box sx={{ display: 'flex', flexShrink: 0 }}>{leadingAction}</Box>}
      <Box className="page-header-content">
        {breadcrumbs && (
          <Box sx={{ mb: 1 }}>
            <Breadcrumb path={breadcrumbs} />
          </Box>
        )}
        <Typography
          variant="h1"
          sx={{
            color: 'primary.main',
            mb: description ? 1 : 0,
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 400, // Belleza is usually better at 400
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: '700px',
              lineHeight: 1.6,
              fontSize: '1.1rem'
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
      {actions && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {actions}
        </Box>
      )}
    </Box>
  );
}
